import { isHiddenFindingDisposition } from 'shared/constants/accessibility/findingDisposition';
import { defaultFindingSource } from './viewModel';

export const FINDING_SUITES = [{
  path: 'accessibility',
  suiteType: 'Accessibility',
  label: 'Accessibility',
}, {
  path: 'functionality',
  suiteType: 'Functionality',
  label: 'Functionality',
}];

const isVisibleFindingSuite = suiteType => FINDING_SUITES.some(suite => suite.suiteType === suiteType);

export const visibleFindingSuites = (suites = []) => (suites || []).filter(suite => isVisibleFindingSuite(suite.suiteType));

export const findingSuite = (result, suiteType) => {
  const suite = result?.findingSuites?.find(candidate => candidate.suiteType === suiteType);
  if (!suite) return { suiteType, findingGroups: [], findingGroupCount: 0, providerProblemCount: 0, pdfObservationCount: 0, occurrenceCount: 0 };
  return {
    ...suite,
    findingGroups: (suite.findingGroups || []).map((group) => {
      const source = defaultFindingSource(group.occurrences?.[0], group.source);
      return {
        ...group,
        source,
        occurrences: group.occurrences.map(occurrence => ({
          ...occurrence,
          source: defaultFindingSource(occurrence, source),
        })),
      };
    }),
  };
};

export const mergeFindingGroups = (currentGroups = [], pageGroups = []) => {
  const groups = new Map(currentGroups.map(group => [group.id, group]));
  pageGroups.forEach((pageGroup) => {
    const current = groups.get(pageGroup.id);
    if (!current) {
      groups.set(pageGroup.id, pageGroup);
      return;
    }
    const occurrences = [...new Map([
      ...(current.occurrences || []),
      ...(pageGroup.occurrences || []),
    ].map(occurrence => [occurrence.id, occurrence])).values()]
      .sort((left, right) => new Date(right.lastSeenAt) - new Date(left.lastSeenAt));
    const states = occurrences.reduce((counts, occurrence) => {
      const state = (occurrence.detectionState || 'unknown').toLowerCase();
      return { ...counts, [state]: (counts[state] || 0) + 1 };
    }, {});
    groups.set(pageGroup.id, {
      ...current,
      ...pageGroup,
      criterionIds: [...new Set([...(current.criterionIds || []), ...(pageGroup.criterionIds || [])])].sort(),
      status: [current.status, pageGroup.status].includes('automated_failure')
        ? 'automated_failure'
        : pageGroup.status || current.status,
      classification: [current, pageGroup].some(({ classification }) => classification === 'unmapped_provider_observation')
        ? 'unmapped_provider_observation'
        : pageGroup.classification,
      occurrenceCount: occurrences.length,
      pageCount: new Set(occurrences.map(({ pageUrl }) => pageUrl).filter(Boolean)).size,
      newCount: states.new || 0,
      recurringCount: states.recurring || 0,
      regressedCount: states.regressed || 0,
      occurrences,
    });
  });
  return [...groups.values()]
    .sort((left, right) => right.occurrenceCount - left.occurrenceCount || left.ruleId.localeCompare(right.ruleId));
};

export const mergeFindingSuitePages = (currentSuites = [], pageSuites = []) => {
  const suites = new Map(visibleFindingSuites(currentSuites).map(suite => [suite.suiteType, suite]));
  visibleFindingSuites(pageSuites).forEach((pageSuite) => {
    const current = suites.get(pageSuite.suiteType);
    suites.set(pageSuite.suiteType, {
      ...current,
      ...pageSuite,
      findingGroups: mergeFindingGroups(current?.findingGroups, pageSuite.findingGroups),
    });
  });
  return [...suites.values()];
};

const isPdfFindingGroup = group => String(group?.id || '').startsWith('pdf:')
  || (group?.occurrences || []).some(occurrence => occurrence.evidenceKind === 'PDF_RULE');

const recountFindingGroup = (group, occurrences) => {
  const states = occurrences.reduce((counts, occurrence) => {
    const state = (occurrence.detectionState || 'unknown').toLowerCase();
    return { ...counts, [state]: (counts[state] || 0) + 1 };
  }, {});
  return {
    ...group,
    occurrences,
    occurrenceCount: occurrences.length,
    pageCount: new Set(occurrences.map(({ pageUrl }) => pageUrl).filter(Boolean)).size,
    newCount: states.new || 0,
    recurringCount: states.recurring || 0,
    regressedCount: states.regressed || 0,
  };
};

export const applyFindingDispositionToSuites = (suites, occurrenceIds, disposition, { includeHidden = false } = {}) => {
  const idSet = new Set(occurrenceIds || []);
  const nextDisposition = !disposition || disposition === 'UNTRIAGED' ? null : disposition;
  const hide = !includeHidden && isHiddenFindingDisposition(nextDisposition);
  return (suites || []).map((suite) => {
    const findingGroups = (suite.findingGroups || []).map((group) => {
      const occurrences = (group.occurrences || [])
        .map(occurrence => (idSet.has(occurrence.id)
          ? { ...occurrence, disposition: nextDisposition }
          : occurrence))
        .filter(occurrence => !(hide && idSet.has(occurrence.id)));
      return recountFindingGroup(group, occurrences);
    }).filter(group => group.occurrenceCount);
    const providerProblemCount = findingGroups
      .filter(group => !isPdfFindingGroup(group) && group.id !== 'broken-link')
      .reduce((total, group) => total + group.occurrenceCount, 0);
    const pdfObservationCount = findingGroups
      .filter(isPdfFindingGroup)
      .reduce((total, group) => total + group.occurrenceCount, 0);
    return {
      ...suite,
      findingGroups,
      findingGroupCount: findingGroups.length,
      providerProblemCount,
      pdfObservationCount,
      occurrenceCount: findingGroups.reduce((total, group) => total + group.occurrenceCount, 0),
    };
  });
};

export const withFindingSuites = (result, findingSuites) => {
  const nextSuites = visibleFindingSuites(findingSuites);
  if (!findingSuites) return result;
  if (!result) {
    return {
      findingSuites: nextSuites,
      findingGroups: nextSuites.find(({ suiteType }) => suiteType === 'Accessibility')?.findingGroups || [],
    };
  }
  const suites = visibleFindingSuites(result.findingSuites).map(summary => ({
    ...summary,
    findingGroups: mergeFindingGroups(
      summary.findingGroups,
      nextSuites.find(({ suiteType }) => suiteType === summary.suiteType)?.findingGroups,
    ),
  }));
  return {
    ...result,
    findingSuites: suites,
    findingGroups: suites.find(({ suiteType }) => suiteType === 'Accessibility')?.findingGroups || [],
  };
};

export const canonicalWebsiteUrl = (value) => {
  try {
    const url = new URL(value);
    url.hash = '';
    const pathname = url.pathname.replace(/\/+$/, '');
    return `${url.origin}${pathname}${url.search}`;
  } catch (_) {
    return value;
  }
};

export const comparableUrl = (value) => {
  try {
    const url = new URL(value);
    url.hash = '';
    return url.toString();
  } catch (_) {
    return value;
  }
};

export const accessibilityFindingGroups = result => result?.findingSuites
  ?.find(({ suiteType }) => suiteType === 'Accessibility')?.findingGroups
  || result?.findingGroups
  || [];

export const findingSuiteLabel = suiteType => FINDING_SUITES.find(suite => suite.suiteType === suiteType)?.label || suiteType;

export const defaultStartScanSelection = (website, {
  canStartAccessibility = true,
  pdfScanningEnabled = true,
} = {}) => ({
  accessibility: Boolean(canStartAccessibility),
  pdf: pdfScanningEnabled !== false,
  brokenLinks: website?.brokenLinksEnabled !== false,
});

export const websiteCheckInputsForStartScan = ({ selection, pages = [], website } = {}) => {
  const jobs = [];
  const crawlKinds = [
    selection?.brokenLinks && 'BROKEN_LINKS',
    selection?.pdf && 'PDF',
  ].filter(Boolean);
  const pasted = pages.length > 0;
  if (selection?.accessibility) {
    jobs.push({
      url: pasted ? pages[0] : website?.url,
      recursive: !pasted,
      checks: ['ACCESSIBILITY'],
      pages: pasted ? pages : undefined,
    });
  }
  if (crawlKinds.length) {
    const crawlUrls = pasted ? pages : [website?.url].filter(Boolean);
    jobs.push(...crawlUrls.map(url => ({
      url,
      recursive: !pasted,
      checks: crawlKinds,
    })));
  }
  return jobs;
};

export const scanUrlsFromText = value => (value || '')
  .split(/\s+/)
  .map(url => url.trim())
  .filter(Boolean);

export const invalidScanUrls = urls => urls.filter((url) => {
  try {
    const parsed = new URL(url);
    return !['http:', 'https:'].includes(parsed.protocol);
  } catch (_) {
    return true;
  }
});
